import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  deleteDoc,
  Unsubscribe,
  serverTimestamp,
  DocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  CollaboratorSalary,
  LegacyCurrentSalarySnapshot,
  SalaryConfig,
  SalaryHistoryEntry,
  hasSalaryPermission,
} from '../Models/collaboratorSalary.types';
import { SalaryHistoryEntryWithId } from '../Models/salaryHistory.types';

export class CollaboratorSalaryService {
  private readonly COLLECTION = 'collaborators_private';
  private readonly SALARY_SUBCOLLECTION = 'salary';
  private readonly HISTORY_SUBCOLLECTION = 'history';
  private readonly CURRENT_SALARY_DOC = 'current';

  private toDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }
    return undefined;
  }

  private parseSalaryDocument(rawData: Record<string, any>): CollaboratorSalary {
    const hasConfigFields =
      rawData.allowRemove !== undefined ||
      rawData.showHistory !== undefined ||
      rawData.createdAt !== undefined ||
      rawData.updatedAt !== undefined;

    const config: SalaryConfig | null = hasConfigFields
      ? {
          allowRemove: rawData.allowRemove ?? true,
          showHistory: rawData.showHistory ?? true,
          createdAt: this.toDate(rawData.createdAt),
          createdBy: rawData.createdBy,
          updatedAt: this.toDate(rawData.updatedAt),
          updatedBy: rawData.updatedBy,
        }
      : null;

    let legacyCurrent: LegacyCurrentSalarySnapshot | null = null;
    if (typeof rawData.currentAmount === 'number') {
      legacyCurrent = {
        amount: rawData.currentAmount,
        extras: rawData.currentExtras ?? 0,
        salaryType: rawData.currentSalaryType ?? 'CLT',
        effectiveDate:
          rawData.effectiveDate ?? new Date().toISOString().slice(0, 10),
      };
    }

    return {
      config,
      legacyCurrent,
    };
  }

  /**
   * Get current salary data for a collaborator
   */
  async getSalary(collaboratorId: string): Promise<CollaboratorSalary | null> {
    try {
      const salaryRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC
      );

      const salaryDoc = await getDoc(salaryRef);

      if (!salaryDoc.exists()) {
        return { config: null, legacyCurrent: null };
      }

      return this.parseSalaryDocument(salaryDoc.data() as Record<string, any>);
    } catch (error) {
      console.error('Error fetching salary:', error);
      throw error;
    }
  }

  /**
   * Update salary and create history entry
   */
  async updateSalary(
    collaboratorId: string,
    newSalary: {
      amount: number;
      extras: number;
      salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
      effectiveDate: string;
    },
    reason: string,
    observations?: string,
    userId?: string
  ): Promise<void> {
    try {
      // Always record the provided salary as a history entry
      await this.addSalaryHistory(collaboratorId, {
        amount: newSalary.amount,
        extras: newSalary.extras,
        salaryType: newSalary.salaryType,
        changeDate: newSalary.effectiveDate,
        changeReason: reason,
        changeObservations: observations,
        recordedAt: new Date(),
        recordedBy: userId || 'system',
      });

      // Ensure configuration document exists and persist audit metadata
      const salaryRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC
      );

      const salaryDoc = await getDoc(salaryRef);
      const timestamp = serverTimestamp();
      const payload: Record<string, any> = {
        updatedAt: timestamp,
        updatedBy: userId || 'system',
      };

      if (!salaryDoc.exists()) {
        payload.allowRemove = true;
        payload.showHistory = true;
        payload.createdAt = timestamp;
        payload.createdBy = userId || 'system';
      }

      await setDoc(salaryRef, payload, { merge: true });
    } catch (error) {
      console.error('Error updating salary:', error);
      throw error;
    }
  }

  /**
   * Get salary history for a collaborator
   */
  async getSalaryHistory(
    collaboratorId: string,
    historyLimit: number = 10
  ): Promise<SalaryHistoryEntry[]> {
    try {
      const historyRef = collection(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC,
        this.HISTORY_SUBCOLLECTION
      );

      const historyQuery = query(
        historyRef,
        orderBy('changeDate', 'desc'),
        limit(historyLimit)
      );

      const historySnapshot = await getDocs(historyQuery);

      return historySnapshot.docs.map(doc => doc.data() as SalaryHistoryEntry);
    } catch (error) {
      console.error('Error fetching salary history:', error);
      throw error;
    }
  }

  /**
   * Get salary history with document IDs
   */
  async getSalaryHistoryWithIds(
    collaboratorId: string,
    historyLimit: number = 10
  ): Promise<SalaryHistoryEntryWithId[]> {
    try {
      const historyRef = collection(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC,
        this.HISTORY_SUBCOLLECTION
      );

      const historyQuery = query(
        historyRef,
        orderBy('changeDate', 'desc'),
        limit(historyLimit)
      );

      const historySnapshot = await getDocs(historyQuery);

      return historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SalaryHistoryEntryWithId));
    } catch (error) {
      console.error('Error fetching salary history with IDs:', error);
      throw error;
    }
  }

  /**
   * Add a new salary history entry
   */
  private async addSalaryHistory(
    collaboratorId: string,
    historyEntry: SalaryHistoryEntry
  ): Promise<void> {
    try {
      const historyRef = collection(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC,
        this.HISTORY_SUBCOLLECTION
      );

      await addDoc(historyRef, historyEntry);
    } catch (error) {
      console.error('Error adding salary history:', error);
      throw error;
    }
  }

  /**
   * Delete a salary history entry (admin only)
   */
  async deleteSalaryHistory(
    collaboratorId: string,
    historyId: string,
    userRoles: string[]
  ): Promise<void> {
    // Check if user has delete permission (admin only)
    if (!hasSalaryPermission(userRoles, 'canDeleteSalary')) {
      throw new Error('Insufficient permissions to delete salary history');
    }

    try {
      const historyRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC,
        this.HISTORY_SUBCOLLECTION,
        historyId
      );

      await deleteDoc(historyRef);
    } catch (error) {
      console.error('Error deleting salary history:', error);
      throw error;
    }
  }

  /**
   * Update an existing salary history entry
   */
  async updateSalaryHistoryEntry(
    collaboratorId: string,
    historyId: string,
    data: {
      amount: number;
      extras: number;
      salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
      changeDate: string;
      changeReason: string;
      changeObservations?: string;
    },
    userId?: string
  ): Promise<void> {
    try {
      const historyRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC,
        this.HISTORY_SUBCOLLECTION,
        historyId
      );

      const existing = await getDoc(historyRef);
      const existingData = existing.data() || {};

      const payload = {
        amount: data.amount,
        extras: data.extras,
        salaryType: data.salaryType,
        changeDate: data.changeDate,
        changeReason: data.changeReason,
        changeObservations: data.changeObservations ?? '',
        recordedAt: existingData.recordedAt ?? serverTimestamp(),
        recordedBy: existingData.recordedBy ?? userId ?? 'system',
        updatedAt: serverTimestamp(),
        updatedBy: userId ?? 'system',
      };

      await setDoc(historyRef, payload, { merge: true });
    } catch (error) {
      console.error('Error updating salary history entry:', error);
      throw error;
    }
  }

  /**
   * Check if user can view salary data
   */
  async canViewSalary(userRoles: string[]): Promise<boolean> {
    return hasSalaryPermission(userRoles, 'canViewSalary');
  }

  /**
   * Check if user can edit salary data
   */
  async canEditSalary(userRoles: string[]): Promise<boolean> {
    return hasSalaryPermission(userRoles, 'canEditSalary');
  }

  /**
   * Set up real-time listener for salary changes
   */
  onSalaryChange(
    collaboratorId: string,
    callback: (salary: CollaboratorSalary | null) => void
  ): Unsubscribe {
    const salaryRef = doc(
      db,
      this.COLLECTION,
      collaboratorId,
      this.SALARY_SUBCOLLECTION,
      this.CURRENT_SALARY_DOC
    );

    return onSnapshot(
      salaryRef,
      (doc: DocumentSnapshot) => {
        if (doc.exists()) {
          callback(
            this.parseSalaryDocument(doc.data() as Record<string, any>)
          );
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error in salary listener:', error);
        callback(null);
      }
    );
  }

  /**
   * Set up real-time listener for salary history changes
   */
  onSalaryHistoryChange(
    collaboratorId: string,
    callback: (history: SalaryHistoryEntryWithId[]) => void,
    historyLimit: number = 10
  ): Unsubscribe {
    const historyRef = collection(
      db,
      this.COLLECTION,
      collaboratorId,
      this.SALARY_SUBCOLLECTION,
      this.CURRENT_SALARY_DOC,
      this.HISTORY_SUBCOLLECTION
    );

    const historyQuery = query(
      historyRef,
      orderBy('changeDate', 'desc'),
      limit(historyLimit)
    );

    return onSnapshot(
      historyQuery,
      (snapshot: QuerySnapshot) => {
        const history = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as SalaryHistoryEntryWithId));
        callback(history);
      },
      (error) => {
        console.error('Error in salary history listener:', error);
        callback([]);
      }
    );
  }

  /**
   * Update salary configuration (allowRemove, showHistory)
   */
  async updateSalaryConfig(
    collaboratorId: string,
    config: {
      allowRemove?: boolean;
      showHistory?: boolean;
    },
    userId?: string
  ): Promise<void> {
    try {
      const salaryRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC
      );

      const salaryDoc = await getDoc(salaryRef);
      const timestamp = serverTimestamp();
      const payload: Record<string, any> = {
        ...config,
        updatedAt: timestamp,
        updatedBy: userId || 'system',
      };

      if (!salaryDoc.exists()) {
        payload.allowRemove = config.allowRemove ?? true;
        payload.showHistory = config.showHistory ?? true;
        payload.createdAt = timestamp;
        payload.createdBy = userId || 'system';
      }

      await setDoc(salaryRef, payload, { merge: true });
    } catch (error) {
      console.error('Error updating salary config:', error);
      throw error;
    }
  }

  /**
   * Initialize salary data for a collaborator (used during migration)
   */
  async initializeSalary(
    collaboratorId: string,
    salaryData: CollaboratorSalary,
    historyEntries?: SalaryHistoryEntry[]
  ): Promise<void> {
    try {
      const salaryRef = doc(
        db,
        this.COLLECTION,
        collaboratorId,
        this.SALARY_SUBCOLLECTION,
        this.CURRENT_SALARY_DOC
      );

      const payload: Record<string, any> = {};

      if (salaryData.config) {
        payload.allowRemove = salaryData.config.allowRemove ?? true;
        payload.showHistory = salaryData.config.showHistory ?? true;
        payload.createdAt = salaryData.config.createdAt ?? serverTimestamp();
        payload.createdBy = salaryData.config.createdBy ?? 'system';
        payload.updatedAt = salaryData.config.updatedAt ?? serverTimestamp();
        payload.updatedBy = salaryData.config.updatedBy ?? 'system';
      }

      if (salaryData.legacyCurrent) {
        payload.currentAmount = salaryData.legacyCurrent.amount;
        payload.currentExtras = salaryData.legacyCurrent.extras;
        payload.currentSalaryType = salaryData.legacyCurrent.salaryType;
        payload.effectiveDate = salaryData.legacyCurrent.effectiveDate;
      }

      await setDoc(salaryRef, payload, { merge: true });

      if (historyEntries && historyEntries.length > 0) {
        for (const entry of historyEntries) {
          await this.addSalaryHistory(collaboratorId, entry);
        }
      }
    } catch (error) {
      console.error('Error initializing salary:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const collaboratorSalaryService = new CollaboratorSalaryService();
